import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { MetricsCard } from "@/components/MetricsCard";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { Users, TrendingUp, Calendar } from "lucide-react";
import { useEffect, useState } from "react";
import { metricsApi } from "@/services/api";

const Analytics = () => {
  const [metrics, setMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    metricsApi.getBusinessMetrics()
      .then(setMetrics)
      .catch(() => setMetrics(null))
      .finally(() => setLoading(false));
  }, []);

  const clientTypes = [
    { name: "Weight Loss", value: 45 },
    { name: "Muscle Gain", value: 30 },
    { name: "Sports Nutrition", value: 15 },
    { name: "Other", value: 10 },
  ];

  const COLORS = ["#4CAF50", "#FFA726", "#42A5F5", "#9C27B0"];

  if (loading) {
    return <div className="text-center py-12">Loading analytics...</div>;
  }
  if (!metrics) {
    return <div className="text-center py-12 text-red-500">Failed to load analytics data.</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Analytics</h1>
        <p className="text-muted-foreground">
          Review your business performance and client statistics
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricsCard 
          title="Total Clients" 
          value={metrics.clientGrowth[metrics.clientGrowth.length - 1]?.clients || 0}
          description="Active clients in your practice"
          data={metrics.clientGrowth}
          dataKey="clients"
          chartColor="#4CAF50"
          percentageChange={undefined}
          icon={<Users className="h-4 w-4 text-muted-foreground" />}
        />
        <MetricsCard 
          title="Monthly Revenue" 
          value={`$${metrics.revenue[metrics.revenue.length - 1]?.amount || 0}`}
          description="Income this month"
          data={metrics.revenue}
          dataKey="amount"
          chartColor="#FFA726"
          percentageChange={undefined}
          icon={<TrendingUp className="h-4 w-4 text-muted-foreground" />}
        />
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Client Retention</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="text-2xl font-bold">{metrics.clientRetention}%</div>
                <p className="text-xs text-muted-foreground">Client retention rate</p>
                <div className="text-xs font-medium text-green-600">
                  +5% from last quarter
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Client Growth</CardTitle>
            <CardDescription>Monthly client acquisition</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={metrics.clientGrowth}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="clients" 
                    name="Clients"
                    stroke="#4CAF50" 
                    strokeWidth={2}
                    activeDot={{ r: 8 }} 
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Revenue</CardTitle>
            <CardDescription>Monthly revenue breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={metrics.revenue}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value) => [`$${value}`, "Revenue"]} 
                  />
                  <Legend />
                  <Bar 
                    dataKey="amount" 
                    name="Revenue" 
                    fill="#FFA726" 
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Client Types</CardTitle>
            <CardDescription>Breakdown by nutrition goal</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={clientTypes}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {clientTypes.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value}%`, "Percentage"]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="col-span-1 md:col-span-2">
          <CardHeader>
            <CardTitle>Key Performance Metrics</CardTitle>
            <CardDescription>Overall business statistics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-500">New Clients This Month</div>
                <div className="text-3xl font-bold">{metrics.newClientsThisMonth}</div>
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-500">Avg. Sessions Per Client</div>
                <div className="text-3xl font-bold">{metrics.averageSessionsPerClient}</div>
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-500">Session Completion Rate</div>
                <div className="text-3xl font-bold">92%</div>
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-500">Avg. Revenue Per Client</div>
                <div className="text-3xl font-bold">$185</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Analytics;
